import { Component, effect, inject, OnInit } from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { MenuItem } from 'primeng/api';
import { PanelMenuModule } from 'primeng/panelmenu';
import { RouterOutlet } from '@angular/router';
import { PermissionService } from '../../../services/permission.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { getState } from '@ngrx/signals';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { NoPermissionsComponent } from '../../atoms/no-permissions/no-permissions.component';

@Component({
  selector: 'app-import-export',
  standalone: true,
  imports: [
    PanelMenuModule,
    RouterOutlet,
    AlertComponent,
    NoPermissionsComponent,
  ],
  templateUrl: './import-export.component.html',
  styleUrl: './import-export.component.scss',
})
export class ImportExportComponent implements OnInit {
  readonly activeUserStore = inject(ActiveUserStore);
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-12',
    icon: 'fas fa-cloud-upload',
    headerText: 'Import und Export',
  };

  items: MenuItem[] = [];
  canAccess = false;

  constructor(
    private sidebarService: SidebarService,
    private permissionService: PermissionService
  ) {
    setTimeout(() => {
      this.sidebarService.setConfiguration(this.sidebarConfig);
    }, 10);

    effect(() => {
      const userState = getState(this.activeUserStore);
      this.buildPanelMenu();
      this.checkAccess();
    });
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * This method initializes the component by building the panel menu.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.buildPanelMenu();
  }

  checkAccess() {
    this.canAccess =
      this.permissionService.canImportIndicators() ||
      this.permissionService.canExportIndicators() ||
      this.permissionService.canImportData() ||
      this.permissionService.canExportData() ||
      this.permissionService.canImportGeoData() ||
      this.permissionService.canExportGeoData() ||
      this.permissionService.canImportPoi() ||
      this.permissionService.canExportPoi();
  }

  /**
   * Builds the panel menu and initializes the `items` property with the menu configuration.
   * Each menu item includes a label, an icon, and a routerLink for navigation.
   *
   * @return {void} This method does not return a value.
   */
  buildPanelMenu(): void {
    this.items = [
      {
        visible:
          this.permissionService.canImportIndicators() ||
          this.permissionService.canExportIndicators(),
        label: 'Indikatoren',
        icon: 'fas fa-ruler-vertical',
        routerLink: [{ outlets: { panel: 'indikatoren' } }],
      },
      {
        visible:
          this.permissionService.canImportData() ||
          this.permissionService.canExportData(),
        label: 'Daten',
        icon: 'fas fa-database',
        routerLink: [{ outlets: { panel: 'daten' } }],
      },
      {
        visible:
          this.permissionService.canImportGeoData() ||
          this.permissionService.canExportGeoData(),
        label: 'Geodaten',
        icon: 'fas fa-circle-nodes',
        routerLink: [{ outlets: { panel: 'geo-daten' } }],
      },
      {
        visible:
          this.permissionService.canImportPoi() ||
          this.permissionService.canExportPoi(),
        label: 'Standorte',
        icon: 'fas fa-location-dot',
        routerLink: [{ outlets: { panel: 'poi' } }],
      },
    ];
  }
}
